/**
 * src/shell/save/schema-validator.ts — JSON Schema 子集校验器。
 *
 * 用途：导入闸门的第一道——直接消费仓库 contracts/*.schema.json 原文
 * （schema 是法律，校验器只读它，不手抄规则，杜绝漂移）。
 * 子集与旧仓 validate_contracts.py 手写校验器同口径：
 * type / required / properties / additionalProperties(false) / items /
 * enum / pattern / minLength / maxLength / minimum / maximum /
 * minItems / maxItems / $ref(#/$defs/x) / $defs。
 * 不支持的关键字出现时按"通过"处理（与旧校验器一致：只验认识的）。
 */

type Schema = Record<string, unknown>

export function validateJsonSchema(
  value: unknown,
  schema: Schema,
  root: Schema = schema,
  path = '$',
): string[] {
  const issues: string[] = []

  if (typeof schema.$ref === 'string') {
    const target = resolveRef(schema.$ref, root)
    if (target) return validateJsonSchema(value, target, root, path)
    return issues // 无法解析的引用按通过处理
  }

  if (schema.enum && Array.isArray(schema.enum)) {
    if (!(schema.enum as unknown[]).includes(value)) {
      issues.push(`${path} 必须是 ${JSON.stringify(schema.enum)} 之一，得到 ${JSON.stringify(value)}`)
    }
    return issues
  }

  const t = schema.type as string | undefined
  if (t && !typeMatches(value, t)) {
    issues.push(`${path} 类型应为 ${t}，得到 ${Array.isArray(value) ? 'array' : typeof value}`)
    return issues // 类型不对则后续检查无意义
  }

  if (typeof value === 'string') {
    if (typeof schema.minLength === 'number' && value.length < schema.minLength) {
      issues.push(`${path} 长度不得少于 ${schema.minLength}`)
    }
    if (typeof schema.maxLength === 'number' && value.length > schema.maxLength) {
      issues.push(`${path} 长度不得超过 ${schema.maxLength}`)
    }
    if (typeof schema.pattern === 'string' && !(new RegExp(schema.pattern).test(value))) {
      issues.push(`${path} 不匹配格式 ${schema.pattern}`)
    }
  }

  if (typeof value === 'number') {
    if (typeof schema.minimum === 'number' && value < schema.minimum) {
      issues.push(`${path} 不得小于 ${schema.minimum}`)
    }
    if (typeof schema.maximum === 'number' && value > schema.maximum) {
      issues.push(`${path} 不得大于 ${schema.maximum}`)
    }
  }

  if (Array.isArray(value)) {
    if (typeof schema.minItems === 'number' && value.length < schema.minItems) {
      issues.push(`${path} 至少需要 ${schema.minItems} 项`)
    }
    if (typeof schema.maxItems === 'number' && value.length > schema.maxItems) {
      issues.push(`${path} 至多允许 ${schema.maxItems} 项`)
    }
    const items = schema.items as Schema | undefined
    if (items) {
      value.forEach((item, i) => {
        issues.push(...validateJsonSchema(item, items, root, `${path}[${i}]`))
      })
    }
  }

  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>
    const props = (schema.properties ?? {}) as Record<string, Schema>
    if (Array.isArray(schema.required)) {
      for (const req of schema.required as string[]) {
        if (!(req in obj)) issues.push(`${path} 缺少必填字段 ${req}`)
      }
    }
    for (const [k, sub] of Object.entries(props)) {
      if (k in obj) issues.push(...validateJsonSchema(obj[k], sub, root, `${path}.${k}`))
    }
    if (schema.additionalProperties === false) {
      for (const k of Object.keys(obj)) {
        if (!(k in props)) issues.push(`${path} 含未定义字段 ${k}`)
      }
    }
  }

  return issues
}

function typeMatches(value: unknown, t: string): boolean {
  switch (t) {
    case 'string':
      return typeof value === 'string'
    case 'number':
      return typeof value === 'number'
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value)
    case 'boolean':
      return typeof value === 'boolean'
    case 'object':
      return value !== null && typeof value === 'object' && !Array.isArray(value)
    case 'array':
      return Array.isArray(value)
    case 'null':
      return value === null
    default:
      return true
  }
}

function resolveRef(ref: string, root: Schema): Schema | null {
  const m = ref.match(/^#\/\$defs\/(.+)$/)
  if (!m) return null
  const defs = (root.$defs ?? {}) as Record<string, Schema>
  return defs[m[1]] ?? null
}
